import React, { useEffect } from 'react';

import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { loadEmployee } from '../../employees/actions/load-employee';
import { employeeMessageTemplatesForOrgIdSelector } from '../../message-templates/message-template-selectors';
import { loadOrg } from '../../organization/actions/load-org';
import { userSelector } from '../../reducers/current-user-selectors';
import { employeeSelector } from '../../reducers/employee-selectors';
import { orgSelector } from '../../reducers/org-selectors';
import { addEmployeeMessageTemplate } from '../actions/messages/add-employee-message-template';
import { loadEmployeeMessageTemplates } from '../actions/messages/load-employee-message-templates';
import { updateEmployeeMessageTemplate } from '../actions/messages/update-employee-message-template';

import { MessageTemplatesDialog } from '../../application/messaging/message-templates-dialog';
import { EmployeeMessageTemplateParser } from '../../message-templates/parsers/employee-message-template-parser';

const mapStateToProps = (state: any, { orgId, employeeId }: InputProps) => ({
  org: orgSelector(state, orgId),
  user: userSelector(state),
  employee: employeeId ? employeeSelector(state, employeeId) : null,
  messageTemplates: employeeMessageTemplatesForOrgIdSelector(state, orgId),
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { orgId, employeeId }: InputProps,
) => ({
  actions: {
    loadOrg: () => dispatch(loadOrg(orgId)),
    loadEmployee: () => employeeId && dispatch(loadEmployee(employeeId)),
    loadEmployeeMessageTemplates: () => dispatch(loadEmployeeMessageTemplates(orgId)),
    addEmployeeMessageTemplate: (template: EmployeeMessageTemplate) =>
      dispatch(addEmployeeMessageTemplate(orgId, template)),
    updateEmployeeMessageTemplate: (template: Partial<EmployeeMessageTemplate>) =>
      dispatch(updateEmployeeMessageTemplate(orgId, template)),
  },
});

type InputProps = {
  orgId: number;
  employeeId?: number | null;
  insertTemplate: (selectedSavedMessage: string) => void;
  templateType: 'MESSAGE' | 'SURVEY';
  messageType: string | null;
  onClose: () => void;
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & StateProps & DispatchProps;

export const EmployeeMessageTemplatesDialog = connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  ({
    messageTemplates,
    employee,
    user,
    org,
    templateType,
    employeeId,
    insertTemplate,
    actions,
    messageType,
    onClose,
  }: Props) => {
    useEffect(() => {
      actions.loadOrg();
      actions.loadEmployee();
      actions.loadEmployeeMessageTemplates();
    });

    if (!org || !messageTemplates) {
      return null;
    }

    const messageTemplatesForType = messageTemplates.filter((t) => t.templateType === templateType);

    const handleUpdateTemplate = async (newTemplate: Record<string, any>) => {
      await actions.updateEmployeeMessageTemplate({ ...newTemplate, templateType });
    };

    const handleCreateTemplate = async (newTemplate: Record<string, any>) => {
      await actions.addEmployeeMessageTemplate({
        ...newTemplate,
        templateType,
      } as EmployeeMessageTemplate);
    };

    const handleDeleteTemplate = async (templateId: number) => {
      await actions.updateEmployeeMessageTemplate({
        employeeMessageTemplateId: templateId,
        isActive: false,
      });
    };

    const parser = new EmployeeMessageTemplateParser({ employee, user, org });

    return (
      <MessageTemplatesDialog
        messageType={messageType}
        templatesForType={messageTemplatesForType}
        parser={parser}
        shouldParseTemplate={Boolean(employeeId)}
        updateTemplate={handleUpdateTemplate}
        createTemplate={handleCreateTemplate}
        deleteTemplate={handleDeleteTemplate}
        templateType={templateType}
        insertTemplate={insertTemplate}
        onClose={onClose}
      />
    );
  },
);
